import React from 'react';
import {Loader2} from 'lucide-react';
import type {OrderTestsQueuedPatient} from '@/shared/types/api';
import {formatBirthdate} from '@/shared/utils/formatBirthdate';
import {PatientEditButton} from '@/modules/provider-console/components/PatientEditModal';

const AVATAR_COLORS = [
    'bg-mld-warning/10 text-mld-warning',
    'bg-sky-100 text-sky-700',
    'bg-violet-100 text-violet-700',
    'bg-mld-danger/10 text-mld-danger',
    'bg-mld-success/10 text-mld-success',
    'bg-orange-100 text-orange-700',
];

function getInitials(first: string, last: string): string {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function getAvatarColor(first: string, last: string): string {
    const hash = (first.charCodeAt(0) || 0) + (last.charCodeAt(0) || 0);
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

interface PatientBannerProps {
    patient: OrderTestsQueuedPatient;
    status: string | null;
    isConnecting?: boolean;
    /** When provided, renders an "Edit" affordance that lets the provider
     *  update the patient's demographics (gender, DOB, name, address). */
    onEdit?: () => void;
}

const PatientBanner: React.FC<PatientBannerProps> = ({patient, status, isConnecting, onEdit}) => {
    const initials = getInitials(patient.firstName, patient.lastName);
    const colorClass = getAvatarColor(patient.firstName, patient.lastName);

    const details: string[] = [];
    if (patient.email) details.push(patient.email);
    details.push(`DOB: ${formatBirthdate(patient.birthDate) || 'N/A'}`);
    details.push(`ID: PT-${patient.userId}`);

    return (
        <div className="flex items-center gap-4 rounded-lg border border-indigo-100 bg-white px-5 py-3">
            <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold ${colorClass}`}>
                {initials}
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">
                    {patient.firstName} {patient.lastName}
                </p>
                <p className="truncate text-xs text-gray-500">{details.join(' · ')}</p>
            </div>
            {onEdit ? <PatientEditButton onClick={onEdit} /> : null}
            {isConnecting ? (
                <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-mld-info/5 px-2.5 py-0.5 text-[10px] font-semibold text-mld-info ring-1 ring-blue-200">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    CONNECTING
                </span>
            ) : status ? (
                <span className="shrink-0 rounded-full bg-mld-warning/10 px-2.5 py-0.5 text-[10px] font-semibold text-mld-warning ring-1 ring-amber-300">
                    {status.toUpperCase()}
                </span>
            ) : null}
        </div>
    );
};

export default PatientBanner;
